'use client';

import { usePathname, useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';
import Link from 'next/link';
import { motion, AnimatePresence } from 'framer-motion';
import {
  LayoutDashboard,
  Users,
  Target,
  Trophy,
  ShoppingBag,
  Award,
  FileQuestion,
  ScrollText,
  LogOut,
  ChevronLeft,
  ChevronRight,
  Smartphone,
  Menu,
  Bell,
  Settings,
  User,
  Heart,
  Building2,
  Ticket,
  Gift,
} from 'lucide-react';
import { ToastProvider } from '@/components/ui';

const navItems = [
  { name: 'Dashboard', path: '/admin/dashboard', icon: LayoutDashboard, color: 'from-blue-500 to-indigo-600' },
  { name: 'Users', path: '/admin/users', icon: Users, color: 'from-emerald-500 to-teal-600' },
  { name: 'Partners', path: '/admin/partners', icon: Building2, color: 'from-teal-500 to-cyan-600' },
  { name: 'Challenges', path: '/admin/challenges', icon: Target, color: 'from-orange-500 to-red-600' },
  { name: 'Competitions', path: '/admin/competitions', icon: Trophy, color: 'from-amber-500 to-yellow-600' },
  { name: 'Store', path: '/admin/store', icon: ShoppingBag, color: 'from-purple-500 to-fuchsia-600' },
  { name: 'Vouchers', path: '/admin/vouchers', icon: Ticket, color: 'from-violet-500 to-purple-600' },
  { name: 'Redemptions', path: '/admin/redemptions', icon: Gift, color: 'from-rose-500 to-pink-600' },
  { name: 'Achievements', path: '/admin/achievements', icon: Award, color: 'from-pink-500 to-rose-600' },
  { name: 'Questionnaires', path: '/admin/questionnaires', icon: FileQuestion, color: 'from-cyan-500 to-sky-600' },
  { name: 'Audit Logs', path: '/admin/audit-logs', icon: ScrollText, color: 'from-slate-500 to-gray-600' },
];

export default function AdminLayout({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const router = useRouter();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [adminUser, setAdminUser] = useState<{ email: string; role: string } | null>(null);

  useEffect(() => {
    setMounted(true);
    const token = localStorage.getItem('admin_token');
    const user = localStorage.getItem('admin_user');
    if (!token && pathname !== '/admin/login') {
      router.push('/admin/login');
    } else {
      setIsAuthenticated(true);
      if (user) {
        try {
          setAdminUser(JSON.parse(user));
        } catch {}
      }
    }
    
    const handleResize = () => {
      if (window.innerWidth < 1024) {
        setSidebarOpen(false);
      } else {
        setSidebarOpen(true);
      }
    };
    
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [pathname, router]);

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
    router.push('/admin/login');
  };

  if (pathname === '/admin/login') {
    return <ToastProvider>{children}</ToastProvider>;
  }

  if (!mounted || !isAuthenticated) {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-gradient-to-br from-indigo-50 via-white to-purple-50">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
          className="w-8 h-8 border-2 border-indigo-600 border-t-transparent rounded-full"
        />
      </div>
    );
  }

  return (
    <ToastProvider>
    <div className="flex min-h-dvh w-full min-w-0 bg-gray-50">
      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setMobileMenuOpen(false)}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 lg:hidden"
          />
        )}
      </AnimatePresence>

      {/* Sidebar */}
      <motion.aside
        initial={false}
        animate={{ 
          width: sidebarOpen ? 220 : 64,
          x: mobileMenuOpen ? 0 : (typeof window !== 'undefined' && window.innerWidth < 1024 && !mobileMenuOpen ? -220 : 0)
        }}
        transition={{ duration: 0.2, ease: 'easeInOut' }}
        className={`fixed left-0 top-0 h-screen bg-slate-900 z-50 flex flex-col
          ${!mobileMenuOpen ? 'hidden lg:flex' : 'flex'}`}
      >
        {/* Logo */}
        <div className="h-12 flex items-center justify-between px-3 border-b border-white/10">
          <AnimatePresence>
            {sidebarOpen && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="flex items-center gap-2"
              >
                <div className="w-7 h-7 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-lg flex items-center justify-center">
                  <Smartphone size={14} className="text-white" />
                </div>
                <span className="text-sm font-bold text-white">ScreenSavvy</span>
              </motion.div>
            )}
          </AnimatePresence>
          <motion.button
            onClick={() => {
              if (window.innerWidth < 1024) {
                setMobileMenuOpen(false);
              } else {
                setSidebarOpen(!sidebarOpen);
              }
            }}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            className="p-1.5 rounded text-slate-400 hover:text-white hover:bg-white/10"
          >
            {sidebarOpen ? <ChevronLeft size={16} /> : <ChevronRight size={16} />}
          </motion.button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-2 px-2 space-y-0.5 overflow-y-auto">
          {navItems.map((item) => {
            const isActive = pathname === item.path;
            const Icon = item.icon;
            return (
              <Link
                key={item.path}
                href={item.path}
                onClick={() => setMobileMenuOpen(false)}
                className={`
                  flex items-center gap-2 px-2.5 py-2 rounded-lg text-xs font-medium
                  transition-all duration-200 relative
                  ${isActive 
                    ? 'text-white bg-white/10' 
                    : 'text-slate-400 hover:text-white hover:bg-white/5'}
                `}
              >
                {isActive && (
                  <motion.div
                    layoutId="activeNav"
                    className={`absolute inset-0 bg-gradient-to-r ${item.color} rounded-lg opacity-80`}
                  />
                )}
                <div className={`relative z-10 w-7 h-7 rounded flex items-center justify-center
                  ${isActive ? 'bg-white/20' : 'bg-white/5'}`}
                >
                  <Icon size={14} />
                </div>
                <AnimatePresence>
                  {sidebarOpen && (
                    <motion.span
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="relative z-10"
                    >
                      {item.name}
                    </motion.span>
                  )}
                </AnimatePresence>
              </Link>
            );
          })}
        </nav>

        {/* User Section */}
        <div className="p-2 border-t border-white/10">
          {sidebarOpen && adminUser && (
            <div className="flex items-center gap-2 px-2 py-1.5 mb-1.5">
              <div className="w-6 h-6 rounded-full bg-indigo-500 flex items-center justify-center text-white text-[10px] font-bold">
                {adminUser.email.charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-white truncate">{adminUser.email}</p>
              </div>
            </div>
          )}
          <motion.button
            onClick={handleLogout}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="w-full flex items-center gap-2 px-2.5 py-2 rounded-lg text-xs font-medium
              bg-red-500/10 text-red-400 hover:bg-red-500 hover:text-white transition-all"
          >
            <LogOut size={14} />
            {sidebarOpen && <span>Logout</span>}
          </motion.button>
        </div>
      </motion.aside>

      {/* Main Content */}
      <div
        className={`flex min-h-dvh min-w-0 w-full flex-1 flex-col transition-all duration-200
        ${sidebarOpen ? 'lg:ml-[220px]' : 'lg:ml-16'}`}
      >
        {/* Compact Header */}
        <header className="sticky top-0 z-30 border-b border-gray-200 bg-white/95 shadow-sm backdrop-blur-sm supports-[backdrop-filter]:bg-white/80">
          <div className="flex h-11 min-h-11 items-center justify-between gap-2 px-3 sm:px-4 lg:px-5">
            {/* Left */}
            <div className="flex items-center gap-2">
              <button
                type="button"
                aria-label="Open menu"
                onClick={() => setMobileMenuOpen(true)}
                className="lg:hidden inline-flex min-h-9 min-w-9 items-center justify-center rounded-lg text-gray-600 hover:bg-gray-100 active:bg-gray-200"
              >
                <Menu size={18} />
              </button>
            </div>

            {/* Right */}
            <div className="flex shrink-0 items-center gap-0.5 sm:gap-1">
              <button
                type="button"
                className="relative inline-flex min-h-9 min-w-9 items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100"
              >
                <Bell size={16} />
                <span className="absolute top-1 right-1 w-1.5 h-1.5 bg-red-500 rounded-full" />
              </button>
              <button
                type="button"
                className="inline-flex min-h-9 min-w-9 items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100"
              >
                <Settings size={16} />
              </button>
              <div className="w-px h-5 bg-gray-200 mx-1" />
              <button
                type="button"
                className="hidden max-w-[40vw] truncate sm:flex sm:max-w-none items-center gap-1.5 rounded-lg bg-indigo-500 py-1 pl-1.5 pr-2 text-xs font-medium text-white"
              >
                <div className="w-5 h-5 rounded bg-white/20 flex items-center justify-center">
                  <User size={12} />
                </div>
                <span>{adminUser?.email.split('@')[0]}</span>
              </button>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <motion.main
          key={pathname}
          initial={{ opacity: 0, y: 5 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.2 }}
          className="flex-1 w-full min-w-0 max-w-full overflow-x-auto p-3 sm:p-4 lg:p-5"
        >
          <div className="w-full min-w-0 max-w-full">{children}</div>
        </motion.main>

        {/* Compact Footer */}
        <footer className="mt-auto border-t border-gray-100 bg-white px-3 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] sm:px-4">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-2 text-xs text-gray-500">
            <p>© {new Date().getFullYear()} ScreenSavvy. All rights reserved.</p>
            <p>
              Developed By Mtoag
            </p>
          </div>
        </footer>
      </div>
    </div>
    </ToastProvider>
  );
}
